import Button from '@pinecast/common/Button';
import ButtonLabel from '@pinecast/common/ButtonLabel';
import {Form} from '@pinecast/common/forms';
import Checkbox from '@pinecast/db-spa/forms/Checkbox';
import TextInput from '@pinecast/db-spa/forms/TextInput';
import {required} from '@pinecast/db-spa/forms/validation';
import {FormattedMessage} from '@pinecast/i18n';

import type {Step} from './DonationPage';
import StepHeading from './StepHeading';
import {useGetSubscriptionCheckoutSession} from './useGetSubscriptionCheckoutSession';

const TipJarRequestLogin = ({
  amount,
  onContinue,
  oneTime,
  slug,
}: {
  amount: number;
  onContinue: (step: Step) => void;
  oneTime: boolean;
  slug: string;
}) => {
  const requestLogin = useGetSubscriptionCheckoutSession();
  return (
    <Form
      initialValues={{receiveUpdates: true}}
      onSubmit={async ({values}: {values: Record<string, any>}) => {
        if (requestLogin.pending) {
          return;
        }
        const result = await requestLogin.action({
          variables: {
            podcastSlug: slug,
            amount,
            email: values.email,
            oneTime,
            receiveUpdates: oneTime ? undefined : !!values.receiveUpdates,
          },
        });
        const nextState = result?.createSubscriptionCheckoutSession?.nextState;
        if (nextState) {
          onContinue(nextState.toLowerCase() as Step);
        }
        // Otherwise the mutation holds the error, which is shown below.
      }}
    >
      <StepHeading>
        <FormattedMessage
          description="Heading of the step of the tip jar page that asks for the listener's email address"
          defaultMessage="Verify your email"
        />
      </StepHeading>
      <TextInput
        autoComplete="email"
        description={
          <FormattedMessage
            description="Copy for the user email"
            defaultMessage="To ensure you can cancel your subscription in the future, we need to verify your email. Where should we send the confirmation link?"
          />
        }
        name="email"
        label={
          <FormattedMessage
            description="Label for the user email"
            defaultMessage="Your email"
          />
        }
        placeholder="me@example.com"
        type="email"
        validate={required}
        validateOn="blur"
      />
      {!oneTime && (
        <Checkbox
          label={
            <FormattedMessage
              description="Checkbox to opt in to email updates from the podcast while subscribing"
              defaultMessage="Email me exclusive updates from this podcast"
            />
          }
          name="receiveUpdates"
        />
      )}
      {/* Why the email could not be sent, such as an address the server
          refuses, shows beside the button, which stays to try again. */}
      <ButtonLabel
        message={requestLogin.error}
        pendingMessage={
          requestLogin.pending ? (
            <FormattedMessage
              description="Announced to screen reader users while the tip jar page sends the email that verifies the listener's address"
              defaultMessage="Sending the confirmation email…"
            />
          ) : null
        }
        style={{flexWrap: 'wrap', marginTop: oneTime ? 0 : 12}}
        type="negative"
      >
        <Button $isPrimary type="submit" pending={requestLogin.pending}>
          <FormattedMessage
            description="Button to verify the user email"
            defaultMessage="Verify email"
          />
        </Button>
      </ButtonLabel>
    </Form>
  );
};

export default TipJarRequestLogin;
